// src/services/nativePurchaseService.ts
// iOS / Android ネイティブ内課金 (RevenueCat)
// Web では PayPal を使うため、このサービスはネイティブプラットフォーム専用。
// 呼び出し前に必ず Capacitor.isNativePlatform() で確認すること。

import { Purchases, LOG_LEVEL, type PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

// RevenueCat API キー（App Store / Google Play 別）
// 本番時は REACT_APP_REVENUECAT_IOS_KEY / REACT_APP_REVENUECAT_ANDROID_KEY で注入する。
const RC_IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY ?? '';
const RC_ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY ?? '';

let _initialized = false;

export async function initializePurchases(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform() || _initialized) return;

  const apiKey = Capacitor.getPlatform() === 'ios' ? RC_IOS_KEY : RC_ANDROID_KEY;
  if (!apiKey) {
    console.warn('[RevenueCat] API key not set. Set REACT_APP_REVENUECAT_IOS_KEY / ANDROID_KEY.');
    return;
  }

  await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
  await Purchases.configure({ apiKey, appUserID: userId });
  _initialized = true;
}

/** 現在のオファリングに含まれる全パッケージを取得 */
export async function getAvailablePackages(): Promise<PurchasesPackage[]> {
  const offerings = await Purchases.getOfferings();
  return offerings.current?.availablePackages ?? [];
}

/** パッケージ identifier を指定して購入 */
export async function purchasePackage(packageIdentifier: string): Promise<boolean> {
  const packages = await getAvailablePackages();
  if (packages.length === 0) {
    // オファリング未設定 / StoreKit が商品を返さない場合。
    // ボタンが無反応に見えないよう、明確なメッセージを投げる。
    throw new Error(
      '現在サブスクリプション商品を読み込めませんでした。時間をおいて再度お試しください。'
    );
  }
  // 指定 identifier が無ければ先頭のパッケージにフォールバック(月額1種のみ想定)
  const pkg = packages.find(p => p.identifier === packageIdentifier) ?? packages[0];

  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return Object.keys(result.customerInfo.entitlements.active).length > 0;
}

/** 購入復元（機種変更・再インストール後に呼ぶ） */
export async function restorePurchases(): Promise<boolean> {
  const { customerInfo } = await Purchases.restorePurchases();
  return Object.keys(customerInfo.entitlements.active).length > 0;
}

/** プレミアムのエンタイトルメントが有効かを確認 */
export async function checkPremiumEntitlement(entitlementId = 'premium'): Promise<boolean> {
  const { customerInfo } = await Purchases.getCustomerInfo();
  return entitlementId in (customerInfo.entitlements.active ?? {});
}
